import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

type Language = 'cs' | 'en' | 'ar';
type Direction = 'ltr' | 'rtl';

interface I18nContextType {
  language: Language;
  direction: Direction;
  setLanguage: (lang: Language) => void;
  t: (key: string, fallback?: string) => string;
}

const I18nContext = createContext<I18nContextType | undefined>(undefined);

const isRtl = (lang: Language): Direction => (lang === 'ar' ? 'rtl' : 'ltr');

const dictionaries: Record<Language, Record<string, string>> = {
  cs: {
    'common.skip_to_content': 'Přeskočit na obsah',
    'common.skip_to_navigation': 'Přeskočit na navigaci',
  },
  en: {
    'common.skip_to_content': 'Skip to content',
    'common.skip_to_navigation': 'Skip to navigation',
  },
  ar: {
    'common.skip_to_content': 'تخطي إلى المحتوى',
    'common.skip_to_navigation': 'تخطي إلى التنقل',
  },
};

export const I18nProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLang] = useState<Language>(() => {
    const saved = localStorage.getItem('lang') as Language | null;
    return saved || 'cs';
  });

  const direction = useMemo<Direction>(() => isRtl(language), [language]);

  useEffect(() => {
    // Set HTML attributes
    document.documentElement.lang = language;
    document.documentElement.dir = direction;
    localStorage.setItem('lang', language);
  }, [language, direction]);

  const setLanguage = (lang: Language) => setLang(lang);

  const t = (key: string, fallback?: string) => {
    const dict = dictionaries[language] || {};
    return dict[key] || fallback || key;
  };

  return (
    <I18nContext.Provider value={{ language, direction, setLanguage, t }}>
      {children}
    </I18nContext.Provider>
  );
};

export const useI18n = (): I18nContextType => {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be used within I18nProvider');
  return ctx;
};

